import { useEffect, useRef } from "react";
import deepResearchStore from "Stores/deepResearchData";
import { getRecentProductEvents } from "Pages/Dashboard/Copilot/agent/copilotTelemetry";

// Per-page context adapters. A page registers a function returning
// { entity, selection, viewState } for what the user is currently looking
// at; unregistered pages degrade to route-level context only.
const adapters = new Map();

const ROUTE_KEYS = [
  ["/sequences", "sequences"],
  ["/contacts", "contacts"],
  ["/inbox", "inbox"],
  ["/calls", "calls"],
  ["/tasks", "tasks"],
  ["/reports", "reports"],
  ["/accounts", "accounts"],
  ["/list-builder", "list_builder"],
  ["/discover", "discover"],
  ["/deep-research", "deep_research"],
  ["/templates", "templates"],
  ["/settings", "settings"],
  ["/dashboard", "dashboard"],
  ["/copilot", "copilot"]
];

const MAX_SELECTION_IDS = 50;

export const registerCopilotContextAdapter = (routeKey, fn) => {
  adapters.set(routeKey, fn);
  return () => {
    if (adapters.get(routeKey) === fn) adapters.delete(routeKey);
  };
};

// Pages register through this hook: the ref keeps the adapter reading live
// state (selection lives in component-local useState) with a single
// registration per mount, so no dependency array can go stale.
export const useCopilotContext = (routeKey, fn) => {
  const ref = useRef(fn);
  ref.current = fn;
  useEffect(
    () => registerCopilotContextAdapter(routeKey, () => ref.current()),
    [routeKey]
  );
};

const routeKeyFor = pathname =>
  (ROUTE_KEYS.find(([prefix]) => pathname.startsWith(prefix)) || [])[1] ||
  "other";

// IDs only — the orchestrator re-fetches fresh data under the user's own
// auth, so nothing here is trusted as fact, only as pointers.
export const getCopilotContext = () => {
  const pathname = window.location.pathname || "";
  const route = routeKeyFor(pathname);
  const context = { v: 1, page: { pathname, route } };
  const sellerProfileId = deepResearchStore.activeSellerProfileId;
  if (sellerProfileId) {
    context.sellerProfileId = sellerProfileId;
  }
  // Built-in: sequence detail id straight from the URL (/sequences/all/:id or
  // /sequences/shared/:id) — the orchestrator resolves id → name/stats itself.
  const seqMatch = pathname.match(/^\/sequences\/(?:all|shared)\/([^/]+)/);
  if (seqMatch) {
    context.page.entity = { type: "sequence", id: seqMatch[1] };
  }
  const adapter = adapters.get(route);
  if (adapter) {
    try {
      const { entity, selection, viewState } = adapter() || {};
      if (entity?.id) context.page.entity = entity;
      if (selection?.ids?.length) {
        context.selection = {
          ...selection,
          ids: selection.ids.slice(0, MAX_SELECTION_IDS),
          total: selection.ids.length
        };
      }
      if (viewState) context.viewState = viewState;
    } catch (e) {
      console.error("copilot context adapter failed", e);
    }
  }
  const recentEvents = getRecentProductEvents();
  if (recentEvents.length) context.recentEvents = recentEvents;
  return context;
};
